package com.eladfinish.bible.contest.ui.components.toolbar.configuration.model

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.ui.graphics.Color
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.StringDesc

@Immutable
sealed interface ToolbarActionConfig<TId : Enum<TId>> {

    val id: TId
    val enabled: Boolean
    val testTag: String
    val placement: ToolbarActionPlacement
    val menuText: StringDesc?
    val visibility: ToolbarActionVisibility
    val overflowPresentation: OverflowPresentation

    data class TextActionRes<TId : Enum<TId>>(
        val stringDesc: StringDesc,
        override val id: TId,
        override val enabled: Boolean = true,
        override val testTag: String = "toolbar_action_button",
        override val placement: ToolbarActionPlacement = ToolbarActionPlacement.OnToolbar,
        override val menuText: StringDesc? = null,
        override val visibility: ToolbarActionVisibility = ToolbarActionVisibility.Always,
        override val overflowPresentation: OverflowPresentation = OverflowPresentation.TextOnly,
        val onClick: () -> Unit,
    ) : ToolbarActionConfig<TId>

    data class TextAction<TId : Enum<TId>>(
        val text: String,
        val color: Color? = null,
        override val id: TId,
        override val enabled: Boolean = true,
        override val testTag: String = "toolbar_action_button",
        override val placement: ToolbarActionPlacement = ToolbarActionPlacement.OnToolbar,
        override val menuText: StringDesc? = null,
        override val visibility: ToolbarActionVisibility = ToolbarActionVisibility.Always,
        override val overflowPresentation: OverflowPresentation = OverflowPresentation.TextOnly,
        val onClick: () -> Unit,
    ) : ToolbarActionConfig<TId>

    data class IconAction<TId : Enum<TId>>(
        val icon: ToolbarIcon,
        val iconColor: Color = Color.White,
        override val id: TId,
        override val enabled: Boolean = true,
        override val testTag: String = "toolbar_action_button",
        override val placement: ToolbarActionPlacement = ToolbarActionPlacement.OnToolbar,
        override val menuText: StringDesc,
        override val visibility: ToolbarActionVisibility = ToolbarActionVisibility.Always,
        override val overflowPresentation: OverflowPresentation = OverflowPresentation.IconAndText,
        val onClick: () -> Unit,
    ) : ToolbarActionConfig<TId> {

        val contentDescription: String
            @Composable
            get() = menuText.localized()
    }

    /**
     * A submenu item that appears in the overflow menu and opens an inner screen-like menu.
     */
    @Immutable
    data class SubMenu<TId : Enum<TId>>(
        override val id: TId,
        val stringDesc: StringDesc,
        val content: SubMenuContent<TId>,
        override val enabled: Boolean = true,
        override val testTag: String = "toolbar_action_button",
        override val placement: ToolbarActionPlacement = ToolbarActionPlacement.InOverflow,
        override val menuText: StringDesc? = null,
        override val visibility: ToolbarActionVisibility = ToolbarActionVisibility.Always,
        override val overflowPresentation: OverflowPresentation = OverflowPresentation.TextOnly,
    ) : ToolbarActionConfig<TId>

    data class CheckboxActionRes<TId : Enum<TId>>(
        override val id: TId,
        val stringDesc: StringDesc,
        val checked: Boolean,
        val onCheckedChange: (Boolean) -> Unit,
        override val placement: ToolbarActionPlacement = ToolbarActionPlacement.InOverflow,
        override val enabled: Boolean = true,
        override val testTag: String = "toolbar_action_button",
        override val menuText: StringDesc? = null,
        override val visibility: ToolbarActionVisibility = ToolbarActionVisibility.Always,
        override val overflowPresentation: OverflowPresentation = OverflowPresentation.TextOnly,
    ) : ToolbarActionConfig<TId>
}
